import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import toast from "react-hot-toast";
import { StandardTableSortablePropertyHeader, StickyThemedTableHeader } from "../../../components/tables/StandardTable";
import { useFilterContext } from "../../../hooks/FilterContext";
import { useLazySearchPortfolioTestRunsQuery } from "../../../services/valuations";
import { useLazyExportTestCertificatePdfQuery, useUpdateMasterAccountFlaggedNotesMutation, useUpdateMasterAccountFlaggedStatusMutation } from "../../../services/valuations/portfolioTestingApiEndpoints";
import { flaggedPortfolioStatuses } from "../components/PortfolioTestResultFilters";

const MasterAccountFlaggedStatusToggle = React.forwardRef(({ className, icon, ...props }, ref) => (
    <FontAwesomeIcon
        className={classNames(className, "has-pointer")}
        ref={ref}
        icon="flag"
        {...props}
    />
));

const MasterAccountFlaggedStatusActionMenuItem = ({ label, uncheckedLabel = null, value, currentValue }) => {
    const showCheck = useMemo(() => value === currentValue, [value, currentValue]);

    return <Dropdown.Item
        className="d-flex justify-content-between align-items-center"
        eventKey={value}
        disabled={showCheck}
    >
        {(!showCheck ? uncheckedLabel : label) ?? label}
        {showCheck && <FontAwesomeIcon
            icon="check"
            className="text-success ms-2"
        />}
    </Dropdown.Item>
}

const MasterAccountFlaggedStatusCell = ({ row, updateFlagStatus, setNoteModalMasterAccount }) => {
    const { masterAccountId, masterAccountName, masterAccountFlagStatus } = row;

    const handleSelect = async (eventKey) => {
        if (eventKey === 'notes') {
            setNoteModalMasterAccount(row);
            return;
        }

        const value = parseInt(eventKey);
        if (!isNaN(value)) {
            await updateFlagStatus(masterAccountId, value, masterAccountName);

            if (value !== 0) // Don't show notes if clearing the flag
                setNoteModalMasterAccount(row);

        }
    }

    const colour = flaggedPortfolioStatuses.find(s => s.value === masterAccountFlagStatus)?.colour;

    return <>
        <Dropdown className="caret-off w-100" onSelect={handleSelect}>
            <Dropdown.Toggle
                as={MasterAccountFlaggedStatusToggle}
                color={colour}
            />
            <Dropdown.Menu>
                {flaggedPortfolioStatuses.map(({ label, uncheckedLabel, value }) =>
                    <MasterAccountFlaggedStatusActionMenuItem
                        key={value}
                        label={label}
                        uncheckedLabel={uncheckedLabel}
                        value={value}
                        currentValue={masterAccountFlagStatus}
                    />
                )}
                {masterAccountFlagStatus !== 0 && <Dropdown.Item eventKey="notes">Add/Edit Notes</Dropdown.Item>}
            </Dropdown.Menu>
        </Dropdown>
    </>;
}

const MasterAccountInformationCell = ({ row }) => {
    const { masterAccountId, masterAccountName, masterAccountFlagNotes } = row;

    const masterAccountLink = <a href={`/client/${masterAccountId}`}>
        {masterAccountName}
    </a>

    return _.isEmpty(masterAccountFlagNotes)
        ? masterAccountLink
        : <OverlayTrigger overlay={(props) => <Tooltip {...props}>{masterAccountFlagNotes}</Tooltip>}>
            {masterAccountLink}
        </OverlayTrigger>
}

const TooltipTableHeader = ({ className = null, index, sortProperty, property, currentSort, onClickSort, children, sort }) => {
    return sort
        ? <StandardTableSortablePropertyHeader
            key={index}
            property={sortProperty ?? property}
            sort={currentSort ?? ""}
            sortTable={onClickSort}
            className={className}
        >
            {children}
        </StandardTableSortablePropertyHeader>
        : <StickyThemedTableHeader key={index} className={className}>
            {children}
        </StickyThemedTableHeader>;
};

const TooltipHeaderRenderer = ({
    label,
    property,
    sortProperty,
    currentSort,
    tooltipText,
    sort = true,
    onClickSort,
    headerClassName = null
}) => {
    const renderTooltip = (props) => (
        <Tooltip {...props}>
            {tooltipText}
        </Tooltip>
    );

    return <TooltipTableHeader
        property={property}
        sortProperty={sortProperty}
        currentSort={currentSort}
        onClickSort={onClickSort}
        sort={sort}
        className={headerClassName}
    >
        <OverlayTrigger overlay={renderTooltip}>
            <span>{label}</span>
        </OverlayTrigger>
    </TooltipTableHeader>
}

const defaultColumns = [
    {
        label: "Master Account",
        CellComponent: MasterAccountInformationCell,
        width: 25
    },
    {
        label: "£",
        property: "totalPortfolioValue",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Total Portfolio Value",
        sort: true,
        headerClassName: "text-center",
        cellFormatter: (value) => value.toLocaleString("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }),
        className: "text-end",
        width: 10
    },
    {
        label: "Target",
        property: "targetProfile",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Client's Target Profile",
        sort: true,
        sortProperty: "targetProfileSortOrder",
        width: 20
    },
    {
        label: "Actual",
        property: "actualProfile",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Client Profile matching actual portfolio volatility score",
        sort: true,
        sortProperty: "actualProfileSortOrder",
        width: 20
    },
    {
        label: "Diff.",
        property: "volatilityDifference",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Difference between target and actual volatility scores",
        sort: true,
        sortProperty: "absoluteVolatilityDifference",
        cellFormatter: (value) => value.toFixed(0) + "%",
        className: "text-end",
        width: 5
    },
    {
        label: "Fund Max",
        property: "fundMaxTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of funds exceeding the maximum % allocation of the target profile",
        sort: true
    },
    {
        label: "Wrapper Max",
        property: "wrapperMaxTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of wrapper rules that failed the maximum % allocation of the target profile",
        sort: true
    },
    {
        label: "Provider Max",
        property: "providerMaxTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of providers exceeding the maximum % allocation of the target profile",
        sort: true
    },
    {
        label: "Vol",
        property: "volatilityTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Total number of Volatility classification tests that failed",
        sort: true
    },
    {
        label: "Fund Max (Vol)",
        property: "volatilityFundMaxTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of funds exceeding the maximum % allocation of a given volatility classification",
        sort: true
    },
    {
        label: "Combined (Vol)",
        property: "volatilityClassGroupTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of funds exceeding the maximum % allocation of a given volatility group rule",
        sort: true
    },
    {
        label: "Specialist Max (Vol)",
        property: "volatilityClassGroupFundMaxTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of funds exceeding the maximum fund allocation of a given volatility group rule",
        sort: true
    },
    {
        label: "Div",
        property: "diversificationTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Total number of Diversification classification tests that failed",
        sort: true
    },
    {
        label: "Fund Max (Div)",
        property: "diversificationFundMaxTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of funds exceeding the maximum % allocation of a given diversification classification",
        sort: true
    },
    {
        label: "Combined (Div)",
        property: "diversificationClassGroupTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of funds exceeding the maximum % allocation of a given diversification group rule",
        sort: true
    },
    {
        label: "Specialist Max (Div)",
        property: "diversificationClassGroupFundMaxTestsFailed",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Number of funds exceeding the maximum fund allocation of a given diversification group rule",
        sort: true
    },
    {
        label: "Score",
        property: "volatilityScore",
        headerRenderer: TooltipHeaderRenderer,
        tooltipText: "Client's actual volatility score",
        sort: true,
        cellFormatter: (value) => value.toFixed(3),
        width: 5
    }
];

const usePortfolioTestResults = () => {
    const [isClearing, setIsClearing] = useState(false);

    const {
        filter,
        isReady
    } = useFilterContext();
    const [sort, setSort] = useState(null);
    const [limit] = useState(50);
    const splitSort = useMemo(() => (sort ?? "").split("+"), [sort]);
    const [cacheKey, setCacheKey] = useState(new Date().valueOf());

    const [search, { data, ...searchResult }] = useLazySearchPortfolioTestRunsQuery();

    const { isFetching, error } = searchResult;

    const { pagination, results } = data || { pagination: { page: 1, pageSize: limit, totalCount: 0, totalPages: 1 }, results: [] };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = useMemo(() => totalCount > 0 && page < totalPages, [page, totalCount, totalPages]);

    const clear = () => {
        if (isFetching || !isReady)
            return;

        const newCacheKey = new Date().valueOf();
        setIsClearing(true);
        search({
            cacheKey: newCacheKey,
            filters: filter,
            page: 1,
            limit,
            sort
        }).unwrap()
            .then(() => setCacheKey(newCacheKey))
            .finally(() => setIsClearing(false));
    };

    useEffect(() => {
        clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sort]);

    const loadMore = () => {
        // Safety check to return early if we are already loading a page
        if (isFetching)
            return;

        search({ cacheKey, filters: filter, page: page + 1, limit, sort });
    };

    const isRowLoaded = useCallback((index) => !hasMore || index < results.length, [hasMore, results]);

    const onClickSort = (property) => {
        setSort(oldSort => {
            const [oldProperty, oldDirection] = (oldSort ?? "").split("+");
            let newDirection = "";

            if (property === oldProperty) {
                newDirection = oldDirection === "DESC" ? null : "+DESC";
            }

            return property + (newDirection ?? "");
        });
    };

    // Add flag column to column defs with update capability
    const [noteModalMasterAccount, setNoteModalMasterAccount] = useState(null);

    const [updateFlagStatusTrigger] = useUpdateMasterAccountFlaggedStatusMutation();
    const [updateFlagNotesTrigger] = useUpdateMasterAccountFlaggedNotesMutation();

    const updateFlagStatus = (masterAccountId, flaggedStatus) =>
        toast.promise(
            updateFlagStatusTrigger({ masterAccountId, flaggedStatus }).unwrap(),
            {
                loading: "Updating flag...",
                success: "Flag status updated!",
                error: "Failed to update flag status"
            }
        );

    const onNoteSave = (masterAccountId, notes) =>
        toast.promise(
            updateFlagNotesTrigger({ masterAccountId, notes }).unwrap().then(() => setNoteModalMasterAccount(null)),
            {
                loading: "Updating flag notes...",
                success: "Flag notes updated",
                error: "Failed to update flag notes",
            }
        );
    const onNoteCancel = () => setNoteModalMasterAccount(null);

    // Add column to open test certificate
    const [openCertificateTrigger] = useLazyExportTestCertificatePdfQuery();

    const openCertificate = (masterAccountId) =>
        toast.promise(
            openCertificateTrigger({ masterAccountId }).unwrap()
                .then(({ data }) => {
                    try {
                        var bytesPdf = Uint8Array.from(window.atob(data), c => c.charCodeAt(0))

                        var file = new Blob([bytesPdf], { type: 'application/pdf' });
                        var fileURL = URL.createObjectURL(file);

                        window.open(fileURL, 'Test Certificate');
                    } catch (ex) {
                        console.error(ex);
                    }
                }),
            {
                loading: "Opening Test Certificate",
                error: "Error opening Test Certificate"
            }
        );

    const columns = [
        {
            property: "masterAccountId",
            cellFormatter: (id) => <OverlayTrigger overlay={(props) => <Tooltip {...props}>Open Test Certificate</Tooltip>}>
                <FontAwesomeIcon className="has-pointer" icon={['far', 'file-lines']} onClick={() => openCertificate(id)} />
            </OverlayTrigger>,
            className: "pe-0"
        },
        {
            className: 'action-col',
            CellComponent: MasterAccountFlaggedStatusCell,
            setNoteModalMasterAccount,
            updateFlagStatus,
            sort: true,
            sortProperty: 'masterAccountFlagStatus',
            overflow: true
        },
        ...defaultColumns
    ];

    return {
        clear,
        isClearing,
        isRowLoaded,
        loadMore,
        currentSort: sort,
        splitSort,
        onClickSort,
        totalCount,
        results: isClearing || error ? [] : results,
        columns,
        noteModalMasterAccount,
        onNoteCancel,
        onNoteSave,
        ...searchResult
    };
}

export default usePortfolioTestResults;